import type { AlertRuleDetailV9 } from '@/api-client/alert/model';
import { AlertRuleV9RequestSubType } from '@/api-client/metav8Over/model/alert-rule-v9-request-sub-type';
import { ALERT_TYPE_VALUE, CHECK_BY_COMBINATION } from '@/alert/utils/define';

/** alert-rules 상세 — api gen에 subType 없을 때 런타임 필드 */
export type AlertRuleDetailV9WithMetricSubType = AlertRuleDetailV9 & {
  subType?: string;
};

export type AlertMetricSubType =
  (typeof AlertRuleV9RequestSubType)[keyof typeof AlertRuleV9RequestSubType];

/** metric subType(CUSTOM/COMPOUND/SINGLE) 판별 입력 — Metric 알람에서만 호출 */
export type AlertMetricSubTypeInput = {
  subType?: string;
  ruleCriteria?: string;
  metricConditions?: Array<MetricConditionTypeSource>;
  child?: unknown[];
  children?: unknown[];
};

/** API child / metricConditions / makeAlertTreeRows 펼침 후 공통 형태 */
export type MetricConditionTypeSource = {
  threshold?: { conditionType?: string };
  conditionType?: string;
};

const FORMULA = 'formula';

export const getMetricConditionType = (source: MetricConditionTypeSource): string | undefined =>
  source.threshold?.conditionType ?? source.conditionType;

export const isFormulaConditionType = (conditionType?: string): boolean =>
  conditionType === FORMULA;

export const isFormulaMetricChild = (source: MetricConditionTypeSource): boolean =>
  isFormulaConditionType(getMetricConditionType(source));

/** Custom 알람: formula child만 유지 (data 참조 메트릭 제외) */
export const filterCustomAlertChildren = <T extends MetricConditionTypeSource>(
  children: T[],
): T[] => children.filter(isFormulaMetricChild);

export const isMetricAlertRuleType = (alertRuleType?: string): boolean =>
  alertRuleType?.toLowerCase() === ALERT_TYPE_VALUE.METRIC.toLowerCase();

const isKnownSubType = (subType?: string): subType is AlertMetricSubType =>
  subType === AlertRuleV9RequestSubType.CUSTOM ||
  subType === AlertRuleV9RequestSubType.COMPOUND ||
  subType === AlertRuleV9RequestSubType.SINGLE;

const getMetricSubTypeSources = (item: AlertMetricSubTypeInput): MetricConditionTypeSource[] => {
  if (item.metricConditions?.length) {
    return item.metricConditions;
  }
  return (item.child ?? item.children ?? []) as MetricConditionTypeSource[];
};

/**
 * Metric subType 판별 순서
 * 1. combi — ruleCriteria (subType enum에 없음, list subType과 별개)
 * 2. API subType — CUSTOM | COMPOUND | SINGLE
 * 3. fallback — custom(formula) → single(1개) → multi(2개+)
 */
export const resolveAlertMetricSubType = (
  item: AlertMetricSubTypeInput,
): AlertMetricSubType | undefined => {
  if (item.ruleCriteria === CHECK_BY_COMBINATION) {
    return undefined;
  }

  if (isKnownSubType(item.subType)) {
    return item.subType;
  }

  const sources = getMetricSubTypeSources(item);
  if (sources.some(isFormulaMetricChild)) {
    return AlertRuleV9RequestSubType.CUSTOM;
  }
  if (sources.length === 1) {
    return AlertRuleV9RequestSubType.SINGLE;
  }
  if (sources.length > 1) {
    return AlertRuleV9RequestSubType.COMPOUND;
  }
  return undefined;
};

type AlertRuleTypeCarrier = AlertMetricSubTypeInput & { alertRuleType?: string };

export const isCustomMetricSubType = (subType?: AlertMetricSubType): boolean =>
  subType === AlertRuleV9RequestSubType.CUSTOM;

export const isCustomAlertRule = (item: AlertRuleTypeCarrier): boolean => {
  if (item.alertRuleType != null && !isMetricAlertRuleType(item.alertRuleType)) {
    return false;
  }
  return resolveAlertMetricSubType(item) === AlertRuleV9RequestSubType.CUSTOM;
};

/** 트리·차트용 — CUSTOM/COMPOUND + combi(ruleCriteria) */
export const isMetricTreeAlertRule = (item: AlertRuleTypeCarrier): boolean => {
  if (item.alertRuleType != null && !isMetricAlertRuleType(item.alertRuleType)) {
    return false;
  }
  if (item.ruleCriteria === CHECK_BY_COMBINATION) {
    return true;
  }

  const resolved = resolveAlertMetricSubType(item);
  if (
    resolved === AlertRuleV9RequestSubType.CUSTOM ||
    resolved === AlertRuleV9RequestSubType.COMPOUND
  ) {
    return true;
  }

  const children = item.child ?? item.children;
  return !item.subType && Array.isArray(children) && children.length > 0;
};

// --- alert detail 실시간·이력 그리드 (setup에서만 사용) ---

/** 룰 상세 + 목록 첫 행으로 subType (child 없어도 CUSTOM 판별) */
export const resolveAlertDetailMetricSubType = ({
  isMetricRule,
  ruleCriteria,
  ruleDetail,
  firstAlertRow,
}: {
  isMetricRule: boolean;
  ruleCriteria?: string;
  ruleDetail?: AlertMetricSubTypeInput | null;
  firstAlertRow?: AlertMetricSubTypeInput;
}): AlertMetricSubType | undefined => {
  if (!isMetricRule) {
    return undefined;
  }
  if (ruleDetail) {
    const fromRule = resolveAlertMetricSubType({
      subType: ruleDetail.subType,
      ruleCriteria,
      metricConditions: ruleDetail.metricConditions,
    });
    if (fromRule) {
      return fromRule;
    }
  }
  if (firstAlertRow) {
    return resolveAlertMetricSubType({
      subType: firstAlertRow.subType,
      ruleCriteria,
      child: firstAlertRow.child ?? firstAlertRow.children,
    });
  }
  return undefined;
};

export const isAlertDetailTreeMode = ({
  metricSubType,
  ruleCriteria,
  hasChildren,
  alertRows = [],
}: {
  metricSubType?: AlertMetricSubType;
  ruleCriteria?: string;
  hasChildren: boolean;
  alertRows?: AlertMetricSubTypeInput[];
}): boolean => {
  if (metricSubType && isMetricTreeAlertRule({ subType: metricSubType, ruleCriteria })) {
    return true;
  }
  if (
    alertRows.some((row) =>
      isMetricTreeAlertRule({
        ...(row as AlertRuleTypeCarrier),
        ruleCriteria,
        child: row.child ?? row.children,
      }),
    )
  ) {
    return true;
  }
  return ruleCriteria === CHECK_BY_COMBINATION || hasChildren;
};

/** makeAlertTreeRows 전 API child 필터 (컬럼 변환 후 conditionType 유실) */
export const prepareAlertDetailTreeSourceRows = <T extends { child?: unknown[] }>(
  rows: T[],
  metricSubType?: AlertMetricSubType,
): T[] =>
  rows.map((row) => {
    const apiChild = row.child as MetricConditionTypeSource[] | undefined;
    const filterChild =
      apiChild?.length &&
      (isCustomMetricSubType(metricSubType) || isCustomAlertRule({ child: apiChild }));
    if (!filterChild) {
      return row;
    }
    return { ...row, child: filterCustomAlertChildren(apiChild) };
  });
